import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { EnrollmentSettingsForm } from "./components/EnrollmentSettingsForm";
import { canAddStateOverride, formattedStates } from "./utils/stateUtils";
import { RENT_REPORTING_SETTINGS_CRUMBS } from "./constants";
import { Loader } from "shared/components/Loader";
import {
  getOperatingStatesAndProvinces,
  updatePartnerDefaultEnrollments,
  EnrollmentSettings
} from "api/landlord/landlord";
import { getErrorMessage } from "utils/error";
import { Redirect } from "shared/components/Redirect";

export type StateOverride = {
  state: string;
  enrollmentType: string;
};

export type PropertyOverride = {
  propertyId: string;
  enrollmentType: string;
  propertyName?: string | null;
};

export type OverrideSection = "state" | "property";

export const RentersSettingsView = () => {
  const [defaultEnrollmentType, setDefaultEnrollmentType] =
    useState<string>("");
  const [stateOverrides, setStateOverrides] = useState<StateOverride[]>([]);
  const [propertyOverrides, setPropertyOverrides] = useState<
    PropertyOverride[]
  >([]);
  const navigate = useNavigate();
  const {
    partnerDefaultEnrollments,
    refreshPartnerDefaultEnrollments,
    permissions
  } = useAuthContext();

  const { data: operatingStates, isLoading: isLoadingStates } = useQuery({
    queryKey: ["OPERATING_STATES_AND_PROVINCES"],
    queryFn: getOperatingStatesAndProvinces
  });

  const originalDefaultTypes = useMemo(() => {
    if (!partnerDefaultEnrollments) return null;

    const mappedStates = partnerDefaultEnrollments.state_province_defaults.map(
      (item) => ({
        state: item.state_province,
        enrollmentType: item.default_enrollment_type
      })
    );

    const mappedProperties = partnerDefaultEnrollments.property_defaults.map(
      (item) => ({
        propertyId: item.property_id,
        enrollmentType: item.default_enrollment_type,
        propertyName: item.property_name ?? undefined
      })
    );

    return {
      defaultEnrollmentType:
        partnerDefaultEnrollments.default_enrollment_type || "na",
      stateOverrides: mappedStates,
      propertyOverrides: mappedProperties
    };
  }, [partnerDefaultEnrollments]);

  useEffect(() => {
    if (!originalDefaultTypes) return;

    setDefaultEnrollmentType(originalDefaultTypes.defaultEnrollmentType);
    setStateOverrides(originalDefaultTypes.stateOverrides);
    setPropertyOverrides(originalDefaultTypes.propertyOverrides);
  }, [originalDefaultTypes]);

  const hasChanges = useMemo(() => {
    if (!originalDefaultTypes) return false;

    const defaultTypeChanged =
      defaultEnrollmentType !== originalDefaultTypes.defaultEnrollmentType;

    const stateOverridesChanged =
      stateOverrides.length !== originalDefaultTypes.stateOverrides.length ||
      stateOverrides.some((override, index) => {
        const original = originalDefaultTypes.stateOverrides[index];
        return (
          override.state !== original.state ||
          override.enrollmentType !== original.enrollmentType
        );
      });

    const propertyOverridesChanged =
      propertyOverrides.length !==
        originalDefaultTypes.propertyOverrides.length ||
      propertyOverrides.some((override, index) => {
        const original = originalDefaultTypes.propertyOverrides[index];
        return (
          override.propertyId !== original.propertyId ||
          override.enrollmentType !== original.enrollmentType
        );
      });

    return (
      defaultTypeChanged || stateOverridesChanged || propertyOverridesChanged
    );
  }, [
    defaultEnrollmentType,
    stateOverrides,
    propertyOverrides,
    originalDefaultTypes
  ]);

  const updateEnrollmentsMutation = useMutation({
    mutationFn: updatePartnerDefaultEnrollments,
    mutationKey: ["UPDATE_PARTNER_DEFAULT_ENROLLMENTS"],
    onSuccess: () => {
      toast.success("Default enrollment settings updated successfully");
      refreshPartnerDefaultEnrollments();
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  const onOverrideAdd = (section: OverrideSection) => {
    if (section === "state")
      setStateOverrides([...stateOverrides, { state: "", enrollmentType: "" }]);
    else
      setPropertyOverrides([
        ...propertyOverrides,
        { propertyId: "", enrollmentType: "" }
      ]);
  };

  const onOverrideChange = (
    section: OverrideSection,
    index: number,
    field: string,
    value: string
  ) => {
    if (section === "state")
      setStateOverrides(
        stateOverrides.map((override, i) =>
          i === index ? { ...override, [field]: value } : override
        )
      );
    else
      setPropertyOverrides(
        propertyOverrides.map((override, i) =>
          i === index ? { ...override, [field]: value } : override
        )
      );
  };

  const onOverrideRemove = (section: OverrideSection, index: number) => {
    if (section === "state")
      setStateOverrides(stateOverrides.filter((_, i) => i !== index));
    else setPropertyOverrides(propertyOverrides.filter((_, i) => i !== index));
  };

  if (!permissions.boomReportWriteAccess) return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs crumbs={RENT_REPORTING_SETTINGS_CRUMBS} />
          </div>
          <div className="px-6 py-4 text-xl font-bold border-b border-alpha-100 text-content-1000 flex justify-between items-center">
            <h1>Rent reporting settings</h1>
          </div>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            onClick={() => navigate("/rent-reporting")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="rent-reporting-settings-form"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            disabled={
              !partnerDefaultEnrollments ||
              updateEnrollmentsMutation.isPending ||
              !hasChanges
            }
            onClick={() => {
              const payload: EnrollmentSettings = {
                default_enrollment_type: defaultEnrollmentType,
                states_and_provinces: stateOverrides.map((override) => ({
                  state_province: override.state,
                  default_enrollment_type: override.enrollmentType
                })),
                properties: propertyOverrides.map((override) => ({
                  property_id: override.propertyId,
                  default_enrollment_type: override.enrollmentType
                }))
              };

              updateEnrollmentsMutation.mutate(payload);
            }}
          >
            Save changes
          </Button>
        </div>
      }
    >
      <div className="max-w-[750px] w-full mx-auto bg-white rounded-lg border border-alpha-100">
        <div className="p-6 space-y-2">
          <h2 className="text-base font-medium text-content-1000">
            Default Enrollment Type
          </h2>
          <p className="text-sm font-medium text-content-300">
            By default, when enrolling renters, this type of enrollment will be
            recommended. The user will be able to override this setting.
          </p>
        </div>
        <div className="p-6 border-t border-alpha-40">
          {!partnerDefaultEnrollments ? (
            <Loader />
          ) : (
            <EnrollmentSettingsForm
              defaultEnrollmentType={defaultEnrollmentType}
              onDefaultEnrollmentTypeChange={setDefaultEnrollmentType}
              stateOverrides={stateOverrides}
              propertyOverrides={propertyOverrides}
              availableStates={formattedStates(
                operatingStates?.operating_states_and_provinces,
                stateOverrides
              )}
              onOverrideAdd={onOverrideAdd}
              onOverrideChange={onOverrideChange}
              onOverrideRemove={onOverrideRemove}
              isLoadingStates={isLoadingStates}
              canAddStateOverride={canAddStateOverride(
                operatingStates?.operating_states_and_provinces,
                stateOverrides
              )}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};
